import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  Plus, X, Check, ShoppingCart, CheckCircle, RotateCcw,
  XCircle, Zap, Eye, Download, Printer, IndianRupee, MessageSquare
} from 'lucide-react';
import api from '../../api/client';
import { openPrintTab } from '../../utils/printHelper';
import { useAuth } from '../../context/AuthContext';
import ConfirmDialog from '../../components/common/ConfirmDialog';

// ── Status config ────────────────────────────────────
const STATUS_META = {
  draft:              { label: 'Draft',              bg: '#f3f4f6', color: '#374151' },
  quote_raised:       { label: 'Quote Raised',       bg: '#fef9c3', color: '#713f12' },
  revision_requested: { label: 'Revision Requested', bg: '#ffedd5', color: '#9a3412' },
  approved:           { label: 'Approved',           bg: '#dbeafe', color: '#1e40af' },
  codes_generated:    { label: 'Codes Generated',    bg: '#dcfce7', color: '#166534' },
  completed:          { label: 'Completed',          bg: '#d1fae5', color: '#065f46' },
  cancelled:          { label: 'Cancelled',          bg: '#fee2e2', color: '#991b1b' },
};
const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.draft;
  return (
    <span style={{
      background: m.bg, color: m.color, fontSize: 11, fontWeight: 600,
      padding: '3px 10px', borderRadius: 12, whiteSpace: 'nowrap',
    }}>{m.label}</span>
  );
};

// ── Review Action Modal ───────────────────────────────
// Used for approve / request-revision / reject
function ReviewModal({ order, action, onClose, onDone }) {
  const [comment, setComment]   = useState('');
  const [loading, setLoading]   = useState(false);

  const isApprove   = action === 'approve';
  const isRevision  = action === 'revision';
  const isReject    = action === 'reject';

  const meta = {
    approve:  { title: '✅ Approve Order',            btn: 'Approve',         cls: 'btn-primary',  commentRequired: false },
    revision: { title: '↩ Request Revision',          btn: 'Send Back',       cls: 'btn-warning',  commentRequired: true  },
    reject:   { title: '❌ Reject & Cancel Order',    btn: 'Cancel Order',    cls: 'btn-danger',   commentRequired: false },
  }[action];

  const handleSubmit = async () => {
    if (meta.commentRequired && !comment.trim()) {
      toast.error('Please enter a comment telling the sales person what to fix'); return;
    }
    setLoading(true);
    try {
      const endpoint = isApprove ? 'approve' : isRevision ? 'request-revision' : 'reject';
      await api.patch(`/orders/${order.id}/${endpoint}`, {
        review_comment: comment.trim() || undefined,
      });
      toast.success(
        isApprove  ? 'Order approved!' :
        isRevision ? 'Revision requested — sales person notified' :
                     'Order cancelled'
      );
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Action failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{meta.title}</span>
          <button className="btn-icon" onClick={onClose}><X size={18}/></button>
        </div>
        <div className="modal-body">
          {/* Order summary */}
          <div style={{ background: 'var(--orange-50)', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--grey-800)', marginBottom: 6 }}>
              {order.order_ref} — {order.school_name}
            </div>
            <div style={{ fontSize: 12, color: 'var(--grey-500)' }}>
              {order.batchName} · {order.tutorial_name} · {order.quantity} codes
            </div>
            {order.price_per_unit && (
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--orange-600)', marginTop: 6 }}>
                ₹{Number(order.price_per_unit).toFixed(2)}/code
                &nbsp;·&nbsp;
                Total: ₹{Number(order.total_value || order.price_per_unit * order.quantity).toFixed(2)}
              </div>
            )}
          </div>

          {/* Comment field */}
          <div className="form-group">
            <label className="form-label">
              {isRevision
                ? <>Comment <span className="req">*</span> <span style={{ fontWeight: 400, color: 'var(--grey-400)' }}>(required — tell sales what to fix)</span></>
                : <>Comment <span style={{ fontWeight: 400, color: 'var(--grey-400)' }}>(optional)</span></>
              }
            </label>
            <textarea className="form-control" rows={3}
              placeholder={
                isApprove  ? 'Any notes for this approval…' :
                isRevision ? 'e.g. Price should be ₹549/book, not ₹499. Please correct and resubmit.' :
                             'Reason for cancellation…'
              }
              value={comment} onChange={e => setComment(e.target.value)}
            />
          </div>

          {isRevision && (
            <div className="alert alert-warning" style={{ fontSize: 12 }}>
              ↩ The sales person will see this comment and must resubmit the quote with corrections.
            </div>
          )}
          {isReject && (
            <div className="alert alert-danger" style={{ fontSize: 12 }}>
              ⚠️ This permanently cancels the order. The sales person cannot resubmit.
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className={`btn ${meta.cls}`} onClick={handleSubmit} disabled={loading}>
            {loading ? 'Processing…' : meta.btn}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Resubmit Modal (sales corrects and resubmits) ────
function ResubmitModal({ order, onClose, onDone }) {
  const { register, handleSubmit, watch, formState: { errors } } = useForm({
    defaultValues: {
      price_per_unit: order.price_per_unit || '',
      quantity:       order.quantity || '',
      notes:          '',
    }
  });
  const price = watch('price_per_unit');
  const qty   = watch('quantity');
  const total = (Number(price) || 0) * (Number(qty) || 0);

  const onSubmit = async (values) => {
    try {
      await api.patch(`/orders/${order.id}/resubmit`, {
        price_per_unit: values.price_per_unit ? Number(values.price_per_unit) : undefined,
        quantity:       values.quantity       ? Number(values.quantity)       : undefined,
        notes:          values.notes?.trim()  || undefined,
      });
      toast.success('Order resubmitted for review');
      onDone();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 500 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title"><RotateCcw size={17}/> Resubmit Quote</span>
          <button className="btn-icon" onClick={onClose}><X size={18}/></button>
        </div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="modal-body">
            {/* Admin's revision comment */}
            {order.review_comment && (
              <div style={{
                background: '#fff7ed', border: '1.5px solid #fed7aa',
                borderRadius: 8, padding: '12px 14px', marginBottom: 16,
              }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#c2410c',
                  textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 6 }}>
                  <MessageSquare size={12} style={{ display: 'inline', marginRight: 4 }}/>
                  Revision Comment from Admin
                </div>
                <p style={{ fontSize: 13, color: '#7c2d12', lineHeight: 1.5 }}>{order.review_comment}</p>
              </div>
            )}

            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Price per Code / Book (₹) <span className="req">*</span></label>
                <input type="number" min={0} step="0.01"
                  className={`form-control ${errors.price_per_unit ? 'error' : ''}`}
                  placeholder="e.g. 549.00"
                  {...register('price_per_unit', { required: 'Required' })} />
                {errors.price_per_unit && <p className="form-error">{errors.price_per_unit.message}</p>}
              </div>
              <div className="form-group">
                <label className="form-label">Quantity</label>
                <input type="number" min={1} max={10000}
                  className="form-control"
                  {...register('quantity')} />
              </div>
            </div>

            {total > 0 && (
              <div style={{
                background: 'var(--orange-50)', border: '1px solid var(--orange-200)',
                borderRadius: 8, padding: '10px 14px', marginBottom: 14,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <span style={{ fontSize: 12, color: 'var(--orange-700)' }}>Revised Total Value</span>
                <span style={{ fontSize: 18, fontWeight: 800, color: 'var(--orange-700)' }}>
                  ₹{total.toFixed(2)}
                </span>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Notes to Admin</label>
              <textarea className="form-control" rows={2}
                placeholder="Explain the corrections made…"
                {...register('notes')} />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary"><RotateCcw size={14}/> Resubmit Quote</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Order Detail Modal ────────────────────────────────
function OrderDetailModal({ orderId, isAdmin, isSales, onClose, onRefresh }) {
  const [order, setCodes_]    = useState(null);
  const [codes, setCodes]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewAction, setReviewAction] = useState(null);  // 'approve'|'revision'|'reject'
  const [showResubmit, setShowResubmit] = useState(false);
  const [generating, setGenerating]     = useState(false);
  const [confirmGen, setConfirmGen]     = useState(false);

  const loadOrder = useCallback(async () => {
    try {
      const { data } = await api.get(`/orders/${orderId}`);
      setCodes_(data.data);
      if (['codes_generated','completed'].includes(data.data.status)) {
        const c = await api.get(`/orders/${orderId}/codes?limit=200`);
        setCodes(c.data.data);
      }
    } catch { toast.error('Failed to load order'); }
    finally { setLoading(false); }
  }, [orderId]);

  useEffect(() => { loadOrder(); }, [loadOrder]);

  const doGenerate = async () => {
    setGenerating(true); setConfirmGen(false);
    try {
      const { data } = await api.post(`/orders/${orderId}/generate-codes`);
      toast.success(`✅ ${data.data.quantity} codes generated!`);
      onRefresh(); await loadOrder();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setGenerating(false); }
  };

  const downloadCodes = () => {
    const rows = ['Code,Vol ID,Print Code,Activated,Disabled'];
    codes.forEach(c => rows.push(`${c.code},${c.vol_id},${c.print_code},${c.stdOne},${c.disable}`));
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const a    = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(blob),
      download: `${order?.order_ref}_codes.csv`
    });
    a.click();
  };

  if (loading) return (
    <div className="modal-overlay"><div className="loader" style={{ margin: 'auto', marginTop: '40vh' }}/></div>
  );

  const o = order;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" style={{ maxWidth: 780 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <span className="modal-title"><ShoppingCart size={18}/> {o?.order_ref}</span>
            <div style={{ marginTop: 4 }}><StatusBadge status={o?.status}/></div>
          </div>
          <button className="btn-icon" onClick={onClose}><X size={18}/></button>
        </div>

        <div className="modal-body">
          {/* Detail grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10, marginBottom: 16 }}>
            {[
              { l: 'School',      v: o?.school_name },
              { l: 'Batch',       v: `${o?.batchName} (${o?.batchCode})` },
              { l: 'Tutorial',    v: `${o?.tutorial_name} [${o?.tutorial_key}]` },
              { l: 'Quantity',    v: o?.quantity },
              { l: 'Raised By',   v: o?.raised_by_name },
              { l: 'Approved By', v: o?.approved_by_name || '—' },
            ].map(x => (
              <div key={x.l} style={{ background: 'var(--grey-50)', borderRadius: 6, padding: '8px 12px' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--orange-600)', textTransform: 'uppercase', letterSpacing: '.07em' }}>{x.l}</div>
                <div style={{ fontSize: 13, fontWeight: 600, marginTop: 2, color: 'var(--grey-800)' }}>{x.v}</div>
              </div>
            ))}
          </div>

          {/* Pricing row */}
          {(o?.price_per_unit || o?.total_value) && (
            <div style={{
              display: 'flex', gap: 16, alignItems: 'center',
              background: 'var(--orange-50)', border: '1px solid var(--orange-200)',
              borderRadius: 8, padding: '10px 16px', marginBottom: 14,
            }}>
              <IndianRupee size={16} style={{ color: 'var(--orange-500)', flexShrink: 0 }}/>
              <div>
                <span style={{ fontSize: 12, color: 'var(--grey-500)' }}>Price per code/book: </span>
                <strong style={{ color: 'var(--orange-700)' }}>
                  ₹{Number(o.price_per_unit).toFixed(2)}
                </strong>
                <span style={{ margin: '0 10px', color: 'var(--grey-300)' }}>·</span>
                <span style={{ fontSize: 12, color: 'var(--grey-500)' }}>Total order value: </span>
                <strong style={{ color: 'var(--orange-700)', fontSize: 15 }}>
                  ₹{Number(o.total_value || o.price_per_unit * o.quantity).toFixed(2)}
                </strong>
              </div>
            </div>
          )}

          {/* Review comment banner */}
          {o?.review_comment && (
            <div style={{
              background: '#fff7ed', border: '1.5px solid #fed7aa',
              borderRadius: 8, padding: '12px 14px', marginBottom: 14,
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#c2410c',
                textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 4 }}>
                <MessageSquare size={12} style={{ display: 'inline', marginRight: 4 }}/>
                Admin Comment
              </div>
              <p style={{ fontSize: 13, color: '#7c2d12', lineHeight: 1.5 }}>{o.review_comment}</p>
            </div>
          )}

          {o?.notes && (
            <div className="alert alert-info" style={{ marginBottom: 14, fontSize: 12 }}>
              <strong>Notes:</strong> {o.notes}
            </div>
          )}

          {/* ── Admin review buttons ── */}
          {isAdmin && ['draft','quote_raised','revision_requested'].includes(o?.status) && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 16 }}>
              <button className="btn btn-primary" style={{ justifyContent: 'center' }}
                onClick={() => setReviewAction('approve')}>
                <CheckCircle size={14}/> Approve
              </button>
              <button className="btn btn-secondary"
                style={{ justifyContent: 'center', color: '#c2410c', borderColor: '#fed7aa' }}
                onClick={() => setReviewAction('revision')}>
                <RotateCcw size={14}/> Request Revision
              </button>
              <button className="btn btn-danger" style={{ justifyContent: 'center' }}
                onClick={() => setReviewAction('reject')}>
                <XCircle size={14}/> Reject / Cancel
              </button>
            </div>
          )}

          {/* ── Sales: resubmit after revision ── */}
          {isSales && o?.status === 'revision_requested' && (
            <div style={{
              background: '#fff7ed', border: '1.5px solid #fed7aa',
              borderRadius: 8, padding: '14px 16px', marginBottom: 16,
            }}>
              <p style={{ fontSize: 13, color: '#9a3412', marginBottom: 12 }}>
                ↩ Admin has requested changes. Review the comment above and resubmit with corrections.
              </p>
              <button className="btn btn-primary" onClick={() => setShowResubmit(true)}>
                <RotateCcw size={14}/> Resubmit with Corrections
              </button>
            </div>
          )}

          {/* ── Generate codes ── */}
          {o?.status === 'approved' && (
            <div style={{ marginBottom: 16 }}>
              <div className="alert alert-success" style={{ marginBottom: 10 }}>
                ✅ Order approved. Ready to generate {o.quantity} student codes.
              </div>
              <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}
                disabled={generating} onClick={() => setConfirmGen(true)}>
                {generating
                  ? 'Generating codes…'
                  : <><Zap size={14}/> Generate {o.quantity} Student Codes</>
                }
              </button>
            </div>
          )}

          {/* ── Code list ── */}
          {codes.length > 0 && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--orange-600)', textTransform: 'uppercase' }}>
                  Generated Codes ({codes.length})
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-sm btn-secondary" onClick={() => openPrintTab(`order/${orderId}`)}>
                    <Printer size={12}/> Print QR
                  </button>
                  <button className="btn btn-sm btn-secondary" onClick={downloadCodes}>
                    <Download size={12}/> Export CSV
                  </button>
                </div>
              </div>
              <div style={{
                border: '1px solid var(--grey-200)', borderRadius: 8,
                maxHeight: 200, overflowY: 'auto',
                display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(130px,1fr))',
                gap: 5, padding: 10,
              }}>
                {codes.map(c => (
                  <div key={c.code} style={{
                    fontFamily: 'monospace', fontSize: 11, fontWeight: 700,
                    background: c.stdOne ? '#dcfce7' : 'var(--grey-50)',
                    color: c.stdOne ? '#166534' : 'var(--grey-700)',
                    padding: '4px 7px', borderRadius: 4, textAlign: 'center',
                    border: `1px solid ${c.stdOne ? '#86efac' : 'var(--grey-200)'}`,
                  }}>
                    {c.code}
                    <div style={{ fontSize: 8, color: 'var(--grey-400)', marginTop: 1 }}>{c.print_code}</div>
                  </div>
                ))}
              </div>
              {o?.code_summary && (
                <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 12, color: 'var(--grey-400)' }}>
                  <span>Total: {o.code_summary.total}</span>
                  <span style={{ color: '#166534' }}>Activated: {o.code_summary.activated}</span>
                  <span>Pending: {Number(o.code_summary.total) - Number(o.code_summary.activated)}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Sub-modals */}
      {reviewAction && (
        <ReviewModal order={o} action={reviewAction} onClose={() => setReviewAction(null)}
          onDone={() => { setReviewAction(null); onRefresh(); onClose(); }} />
      )}
      {showResubmit && (
        <ResubmitModal order={o} onClose={() => setShowResubmit(false)}
          onDone={() => { setShowResubmit(false); onRefresh(); onClose(); }} />
      )}
      <ConfirmDialog open={confirmGen} title="Generate Student Codes"
        message={`Generate ${o?.quantity} unique codes for ${o?.school_name}? This cannot be undone.`}
        confirmLabel="Generate Codes" danger={false}
        onConfirm={doGenerate} onCancel={() => setConfirmGen(false)} />
    </div>
  );
}

// ── Main Orders Page ──────────────────────────────────
export default function OrdersPage() {
  const { user, can } = useAuth();
  const isAdmin = ['super_admin','business_admin'].includes(user?.roleName || user?.role_name);
  const isSales = user?.roleName === 'sales' || user?.role_name === 'sales';

  const [orders, setOrders]     = useState([]);
  const [stats, setStats]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [modal, setModal]       = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');

  // Create form state
  const [schools, setSchools]       = useState([]);
  const [batches, setBatches]       = useState([]);
  const [batchTuts, setBatchTuts]   = useState([]);

  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm();
  const watchBatch = watch('batch_id');
  const watchQty   = watch('quantity', 0);
  const watchPrice = watch('price_per_unit', 0);
  const totalValue = (Number(watchQty)||0) * (Number(watchPrice)||0);

  useEffect(() => {
    if (watchBatch) {
      api.get(`/batches/${watchBatch}/tutorials`).then(r => setBatchTuts(r.data.data)).catch(() => setBatchTuts([]));
    } else setBatchTuts([]);
  }, [watchBatch]);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set('status', filterStatus);
      const [o, s] = await Promise.all([
        api.get(`/orders?${params}`),
        isAdmin ? api.get('/orders/stats') : Promise.resolve({ data: { data: null } }),
      ]);
      setOrders(o.data.data);
      setStats(s.data.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [filterStatus, isAdmin]);

  useEffect(() => { load(); }, [load]);

  const openCreate = async () => {
    reset({ school_id:'', batch_id:'', tutorial_id:'', quantity:1, price_per_unit:'', notes:'' });
    try {
      const [sc, ba] = await Promise.all([api.get('/schools'), api.get('/batches')]);
      setSchools(sc.data.data);
      setBatches(ba.data.data.filter(b => !b.disable));
    } catch { toast.error('Failed to load data'); return; }
    setModal(true);
  };

  const onSubmit = async (values) => {
    try {
      const { data } = await api.post('/orders', {
        ...values,
        quantity: Number(values.quantity),
        price_per_unit: values.price_per_unit ? Number(values.price_per_unit) : undefined,
      });
      toast.success(data.message);
      setModal(false); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  // Stats cards
  const StatCard = ({ label, value, color }) => (
    <div style={{
      background: 'var(--white)', border: '1px solid var(--orange-100)',
      borderRadius: 10, padding: '14px 18px', textAlign: 'center',
    }}>
      <div style={{ fontSize: 22, fontWeight: 800, color }}>{value ?? 0}</div>
      <div style={{ fontSize: 10, color: 'var(--grey-400)', marginTop: 2, textTransform: 'uppercase', letterSpacing: '.06em' }}>{label}</div>
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Orders</h1>
          <p className="page-subtitle">Quote → Review → Approve → Generate student codes</p>
        </div>
        {can('orders','create') && (
          <button className="btn btn-primary" onClick={openCreate}><Plus size={15}/> Raise Order</button>
        )}
      </div>

      {/* Stats */}
      {isAdmin && stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(110px,1fr))', gap: 10, marginBottom: 20 }}>
          <StatCard label="Total"           value={stats.total}              color="var(--grey-800)" />
          <StatCard label="Quote Raised"    value={stats.quote_raised}       color="#92400e" />
          <StatCard label="Revision Req."   value={stats.revision_requested} color="#9a3412" />
          <StatCard label="Approved"        value={stats.approved}           color="#1e40af" />
          <StatCard label="Codes Generated" value={stats.codes_generated}    color="#166534" />
          <StatCard label="Cancelled"       value={stats.cancelled}          color="#991b1b" />
          {stats.total_value > 0 && (
            <div style={{
              background: 'var(--orange-50)', border: '1px solid var(--orange-200)',
              borderRadius: 10, padding: '14px 18px', textAlign: 'center', gridColumn: 'span 2',
            }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--orange-700)' }}>
                ₹{Number(stats.total_value).toLocaleString('en-IN')}
              </div>
              <div style={{ fontSize: 10, color: 'var(--orange-500)', marginTop: 2, textTransform: 'uppercase', letterSpacing: '.06em' }}>
                Total Order Value
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pending alerts */}
      {isAdmin && stats?.quote_raised > 0 && (
        <div className="alert alert-warning" style={{ marginBottom: 16 }}>
          ⚠️ <strong>{stats.quote_raised} quote{stats.quote_raised > 1 ? 's' : ''}</strong> awaiting your review and approval.
        </div>
      )}
      {isSales && orders.filter(o => o.status === 'revision_requested').length > 0 && (
        <div className="alert alert-warning" style={{ marginBottom: 16 }}>
          ↩ <strong>{orders.filter(o => o.status === 'revision_requested').length} order{orders.filter(o => o.status === 'revision_requested').length > 1 ? 's' : ''}</strong> need your correction — click View to resubmit.
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <select className="form-control" style={{ width: 190 }}
              value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="">All Statuses</option>
              {Object.entries(STATUS_META).map(([k,v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
            <span className="badge badge-orange">{orders.length} orders</span>
          </div>
        </div>
        <div className="table-wrap">
          {loading ? <div className="loader-wrap"><div className="loader"/></div> : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Order Ref</th>
                  <th>School</th>
                  <th>Batch / Tutorial</th>
                  <th>Qty</th>
                  <th>Price/Code</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th>Raised By</th>
                  <th>Date</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {orders.map(o => (
                  <tr key={o.id}>
                    <td>
                      <code style={{ fontSize: 11, background: 'var(--grey-100)', padding: '2px 6px', borderRadius: 4 }}>
                        {o.order_ref}
                      </code>
                    </td>
                    <td style={{ fontWeight: 500, fontSize: 13 }}>{o.school_name}</td>
                    <td>
                      <div style={{ fontSize: 13 }}>{o.batchName}</div>
                      <div style={{ fontSize: 11, color: 'var(--grey-400)' }}>{o.tutorial_name}</div>
                    </td>
                    <td style={{ fontWeight: 700 }}>{o.quantity}</td>
                    <td style={{ fontSize: 13, color: 'var(--orange-600)', fontWeight: 600 }}>
                      {o.price_per_unit ? `₹${Number(o.price_per_unit).toFixed(2)}` : <span style={{ color: 'var(--grey-300)' }}>—</span>}
                    </td>
                    <td style={{ fontSize: 13, fontWeight: 700 }}>
                      {o.total_value ? `₹${Number(o.total_value).toLocaleString('en-IN')}` : <span style={{ color: 'var(--grey-300)' }}>—</span>}
                    </td>
                    <td><StatusBadge status={o.status}/></td>
                    <td style={{ fontSize: 12 }}>{o.raised_by_name}</td>
                    <td style={{ fontSize: 11, color: 'var(--grey-400)' }}>
                      {new Date(o.created_at).toLocaleDateString('en-IN')}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 5 }}>
                        <button className="btn btn-sm btn-secondary" onClick={() => setDetailId(o.id)}>
                          <Eye size={12}/> View
                        </button>
                        {o.status === 'codes_generated' && (
                          <button className="btn-icon" title="Print QR"
                            onClick={() => openPrintTab(`order/${o.id}`)}>
                            <Printer size={13}/>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {!orders.length && (
                  <tr><td colSpan={10}>
                    <div className="empty-state">
                      <ShoppingCart size={36}/>
                      <h3>No orders yet</h3>
                      <p>Raise your first order to generate student codes.</p>
                    </div>
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Create Order Modal ────────────────────── */}
      {modal && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title"><ShoppingCart size={18}/> Raise Order / Quote</span>
              <button className="btn-icon" onClick={() => setModal(false)}><X size={18}/></button>
            </div>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="modal-body">
                <div className="alert alert-info" style={{ marginBottom: 16, fontSize: 12 }}>
                  {isSales
                    ? '📋 This raises a quote for Business Admin approval. Set your price per book so Admin can review the value.'
                    : '📋 As Business Admin you can raise an order directly.'}
                </div>

                <div className="form-group">
                  <label className="form-label">School <span className="req">*</span></label>
                  <select className={`form-control ${errors.school_id?'error':''}`}
                    {...register('school_id',{required:'Required'})}>
                    <option value="">Select school…</option>
                    {schools.map(s => <option key={s.id} value={s.id}>{s.school_name} — {s.address}</option>)}
                  </select>
                  {errors.school_id && <p className="form-error">{errors.school_id.message}</p>}
                </div>

                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Batch <span className="req">*</span></label>
                    <select className={`form-control ${errors.batch_id?'error':''}`}
                      {...register('batch_id',{required:'Required'})}>
                      <option value="">Select batch…</option>
                      {batches.map(b => <option key={b.id} value={b.id}>{b.batchName} [{b.batchCode}]</option>)}
                    </select>
                    {errors.batch_id && <p className="form-error">{errors.batch_id.message}</p>}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Tutorial <span className="req">*</span></label>
                    <select className={`form-control ${errors.tutorial_id?'error':''}`}
                      {...register('tutorial_id',{required:'Required'})}
                      disabled={!watchBatch||!batchTuts.length}>
                      <option value="">{!watchBatch?'Select batch first…':!batchTuts.length?'No tutorials in batch':'Select tutorial…'}</option>
                      {batchTuts.map(t => <option key={t.id} value={t.id}>{t.name} [{t.key_value}]</option>)}
                    </select>
                    {errors.tutorial_id && <p className="form-error">{errors.tutorial_id.message}</p>}
                    {watchBatch && !batchTuts.length && (
                      <p className="form-error">Assign tutorials to this batch first via Batches page.</p>
                    )}
                  </div>
                </div>

                {/* Quantity + Price in same row */}
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Number of Codes (Books) <span className="req">*</span></label>
                    <input type="number" min={1} max={10000}
                      className={`form-control ${errors.quantity?'error':''}`}
                      placeholder="e.g. 200"
                      {...register('quantity',{required:'Required',min:{value:1,message:'Min 1'}})} />
                    {errors.quantity && <p className="form-error">{errors.quantity.message}</p>}
                  </div>
                  <div className="form-group">
                    <label className="form-label">
                      Price per Code / Book (₹)
                      {isSales && <span className="req"> *</span>}
                    </label>
                    <input type="number" min={0} step="0.01"
                      className={`form-control ${errors.price_per_unit?'error':''}`}
                      placeholder="e.g. 499.00"
                      {...register('price_per_unit', isSales ? {required:'Sales must set a price'} : {})} />
                    {errors.price_per_unit && <p className="form-error">{errors.price_per_unit.message}</p>}
                    <p style={{ fontSize: 11, color: 'var(--grey-400)', marginTop: 3 }}>
                      Admin will review this price before approving
                    </p>
                  </div>
                </div>

                {/* Live total */}
                {totalValue > 0 && (
                  <div style={{
                    background: 'var(--orange-50)', border: '1px solid var(--orange-200)',
                    borderRadius: 8, padding: '10px 16px', marginBottom: 14,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}>
                    <span style={{ fontSize: 13, color: 'var(--orange-700)' }}>Quote Total Value</span>
                    <span style={{ fontSize: 20, fontWeight: 800, color: 'var(--orange-700)' }}>
                      ₹{totalValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea className="form-control" rows={2}
                    placeholder="Any special instructions…"
                    {...register('notes')} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">
                  <ShoppingCart size={14}/>
                  {isSales ? 'Raise Quote for Review' : 'Create Order'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Order Detail */}
      {detailId && (
        <OrderDetailModal
          orderId={detailId}
          isAdmin={isAdmin}
          isSales={isSales}
          onClose={() => setDetailId(null)}
          onRefresh={load}
        />
      )}
    </div>
  );
}
